import { cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../../lib/utils";

export type AvatarSize = 16 | 20 | 24 | 28 | 32 | 40 | 48 | 120;

export interface AvatarProps {
	/**
	 * Size of the avatar
	 */
	size?: AvatarSize;
	/**
	 * The alt attribute for the avatar image
	 */
	alt?: string;
	/**
	 * The id of the user to render the avatar for
	 */
	id?: string;
	/**
	 * Border around the avatar
	 */
	border?: boolean;
	/**
	 * Classname to override the default styles
	 */
	dangerouslySetClassName?: string;
}

const getAvatarURL = (id: string) => {
	const mediaCDN = `${
		window.PERFORMYARD_MEDIA_CDN ? window.PERFORMYARD_MEDIA_CDN : "https://performyard-content.performyard.com/"
	}`;

	return `${mediaCDN}avatar/${id}/`;
};

/**
 * Component for rendering a user's avatar image. If no id is provided, a default anonymous avatar will be rendered.
 */
const avatarVariants = cva("size-5 rounded-full object-cover", {
	variants: {
		size: {
			16: "size-4",
			20: "size-5",
			24: "size-6",
			28: "size-7",
			32: "size-8",
			40: "size-10",
			48: "size-12",
			120: "size-[120px]",
		},
		border: {
			true: "border border-solid border-neutral-10",
		},
	},
	defaultVariants: {
		size: 40,
		border: false,
	},
});

export const Avatar = (props: AvatarProps) => {
	const { size = 40, alt = "Avatar", id = "", dangerouslySetClassName, border } = props;

	const anonymousAvatarURL = "https://dev-media.performyard.com/static/img/anonymous.png";

	let src = "";

	if (!id) {
		src = anonymousAvatarURL;
	} else {
		src = getAvatarURL(id);
	}

	return <img className={cn(avatarVariants({ size, border }), dangerouslySetClassName)} alt={alt} src={src} />;
};
